<template>
    <div class="conversion-path-wrapper">
        <div v-if="loading" class="text-center my-4">
            <b-spinner variant="primary" />
        </div>

        <ErrorMessage v-if="error" :value="error" class="mb-2" @dismissed="error = null" />

        <div v-if="!loading && !error" ref="wrapper" class="wrapper">
            <div class="timeline-wrapper">
                <!-- SOURCE -->
                <div class="timeline-item">
                    <div class="timeline-item-header">
                        <div class="timeline-item-header-title">
                            <div class="d-flex align-items-center">
                                <img class="timeline-item-header-title-img" src="@/assets/source.png" :alt="$t('conversionPath.source')">
                                {{ $t('conversionPath.source') }}
                            </div>
                        </div>
                    </div>
                    <div class="timeline-item-bar">
                        <div class="timeline-item-bar-line">
                            <div class="timeline-item-bar-line-top"></div>
                            <div class="timeline-item-bar-line-point"></div>
                            <div class="timeline-item-bar-line-bottom"></div>
                        </div>
                    </div>
                    <div class="timeline-item-description">
                        <div class="timeline-item-description-line"></div>
                        <div class="timeline-item-description-inner">
                            <div class="timeline-item-description-header">
                                <img src="@/assets/source2.png" :alt="$t('conversionPath.transactionSource')">
                                <span class="timeline-item-description-header-label">{{ $t('conversionPath.transactionSource') }}</span>
                            </div>
                            <span>utm_source: {{ transaction.source || $t('unknownSource').toLowerCase() }}</span>
                        </div>
                    </div>
                </div>

                <!-- PRODUCER PAGE -->
                <div class="timeline-item">
                    <div class="timeline-item-header">
                        <div class="timeline-item-header-title">
                            <div class="d-flex align-items-center">
                                <img class="timeline-item-header-title-img" src="@/assets/producer-page.png" :alt="$t('conversionPath.producerPage')">
                                {{ $t('conversionPath.producerPage') }}
                            </div>
                            <div class="timeline-item-header-title-info">
                                {{ $t('conversionPath.clickTime') }}: {{ $d(new Date(transaction.createdAt - transaction.conversionTime), 'longNumeric') }}
                            </div>
                        </div>
                    </div>
                    <div class="timeline-item-bar">
                        <div class="timeline-item-bar-line">
                            <div class="timeline-item-bar-line-top"></div>
                            <div class="timeline-item-bar-line-point"></div>
                            <div class="timeline-item-bar-line-bottom"></div>
                        </div>
                    </div>
                    <div class="timeline-item-description">
                        <div
                            class="timeline-item-description-line"
                            :class="{ short: transaction.clickedModel && products.some(product => transaction.clickedEan === product.ean) }"
                        ></div>
                        <div class="timeline-item-description-inner">
                            <div class="timeline-item-description-header">
                                <img src="@/assets/icon-clicked-offer.svg" :alt="$t('conversionPath.clickedOffer')">
                                <span class="timeline-item-description-header-label">{{ $t('conversionPath.clickedOffer') }}</span>
                            </div>
                            <span v-if="transaction.clickedModel" class="offer-model">{{ transaction.clickedModel }}</span>
                            <span v-if="transaction.clickedEan" :class="{ 'offer-ean': true, 'offer-ean--bold': !transaction.clickedModel }">
                                {{ $t('conversionPath.ean') }}: {{ transaction.clickedEan }}
                            </span>
                            <span v-if="!transaction.clickedModel && !transaction.clickedEan">{{ $t('conversionPath.unidentifiedProduct') }}</span>
                        </div>
                    </div>
                </div>

                <!-- SHOP PAGE -->
                <div class="timeline-item">
                    <div class="timeline-item-time">
                        <img src="@/assets/icon-conversion-time.svg" alt="">
                        <span class="timeline-item-time-value">{{ conversionTimeFormatted }}</span>
                    </div>
                    <div class="timeline-item-header">
                        <div class="timeline-item-header-title">
                            <div class="d-flex align-items-center">
                                <img class="timeline-item-header-title-img" src="@/assets/shop-page.png" :alt="$t('conversionPath.showPage')">
                                {{ $t('conversionPath.showPage') }}
                            </div>
                            <div class="timeline-item-header-title-info">{{ $t('conversionPath.buyTime') }}: {{ $d(new Date(transaction.createdAt), 'longNumeric') }}</div>
                        </div>
                    </div>
                    <div class="timeline-item-bar">
                        <div class="timeline-item-bar-line">
                            <div class="timeline-item-bar-line-top"></div>
                            <div class="timeline-item-bar-line-point"></div>
                            <div class="timeline-item-bar-line-bottom"></div>
                        </div>
                    </div>
                    <div class="timeline-item-description">
                        <div
                            class="timeline-item-description-line"
                            :class="{ 'd-none': transaction.clickedModel && products.some(product => transaction.clickedEan === product.ean) }"
                        ></div>
                        <div class="timeline-item-description-inner">
                            <div class="timeline-item-description-header">
                                <img src="@/assets/icon-transactions.svg" :alt="$t('conversionPath.transaction')">
                                <span class="timeline-item-description-header-label">{{ $t('conversionPath.transaction') }}</span>
                            </div>
                            <table v-if="products && products.length" class="transaction-table">
                                <thead>
                                    <tr>
                                        <td>{{ $t('conversionPath.product') }}</td>
                                        <td>{{ $t('conversionPath.ean') }}</td>
                                        <td>{{ $t('conversionPath.brand') }}</td>
                                        <td>{{ $t('conversionPath.quantity') }}</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(product, index) in products" :key="index">
                                        <td
                                            :ref="transaction.clickedModel && transaction.clickedEan === product.ean ? 'transactionMarkedProduct' : undefined"
                                            :class="{ 'transaction-marked-product': transaction.clickedModel && transaction.clickedEan === product.ean }"
                                        >
                                            <div class="inner-wrapper">
                                                <span v-if="product.product || product.ean">{{ product.product }}</span>
                                                <span v-if="!product.product && !product.ean">
                                                    {{ $t('conversionPath.unknownProduct') }}
                                                    <Tooltip :text="$t('conversionPath.unknownProductDescription')">
                                                        <i class="fa fa-question-circle ml-1"></i>
                                                    </Tooltip>
                                                </span>
                                            </div>
                                        </td>
                                        <td :class="{ 'transaction-marked-product': !transaction.clickedModel && transaction.clickedEan === product.ean }">{{ product.ean }}</td>
                                        <td>{{ product.brand }}</td>
                                        <td>{{ product.quantity }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import ErrorMessage from '@/components/common/ErrorMessage.vue';
    import Tooltip from '@/components/common/Tooltip.vue';

    export default {
        name: 'ConversionPath',
        components: { ErrorMessage, Tooltip },
        props: {
            transaction: {
                type: Object,
                required: true,
            },
        },
        inject: { parentComponent: { default: null } },
        data() {
            return {
                requestProductDetails: this.$req('reportsSales/ProductDetails'),
                products: [],
                error: null,
            };
        },
        computed: {
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            timeRange() {
                return this.parentComponent?.timeRange || null;
            },
            loading() {
                return this.requestProductDetails.getLoading();
            },
            conversionTimeFormatted() {
                const totalSeconds = Math.max(0, Math.round((Number(this.transaction.conversionTime) || 0) / 1000));
                const hours = Math.floor(totalSeconds / 3600);
                const minutes = Math.floor((totalSeconds % 3600) / 60);
                const seconds = totalSeconds % 60;
                return [hours, minutes, seconds].map(v => String(v).padStart(2, '0')).join(':');
            },
        },
        watch: {
            async transaction(newTransaction, oldTransaction) {
                if (newTransaction?.id !== oldTransaction?.id) {
                    await this.getProducts();
                }
            },
        },
        methods: {
            async getProducts() {
                try {
                    if (!this.spaceId || !this.timeRange || !this.transaction?.id) return;

                    const { startDate, endDate } = this.timeRange;
                    this.error = null;

                    const params = {
                        urlParams: { spaceId: this.spaceId },
                        requestParams: {
                            startDate,
                            endDate,
                        },
                    };

                    const data = await this.requestProductDetails.get(params);
                    const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
                    this.products = items.filter(item => item.id === this.transaction.id);
                } catch (e) {
                    this.error = e?.message || this.$t('error');
                    this.products = [];
                }
            },
        },
        async mounted() {
            await this.getProducts();
        },
    };
</script>

<style lang="scss" scoped>
    $lineColor: #bdbdbd;
    $textColor: #757575;
    $timelineHeaderHeight: 130px;
    $timelineBarHeight: 14px;
    $timelineBarTopMargin: 30px;
    $timelineBarBottomMargin: 120px;

    .conversion-path-wrapper {
        position: relative;
    }

    .wrapper {
        position: relative;
    }

    .timeline-wrapper {
        overflow: hidden;
        display: flex;
    }

    .timeline-item {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        align-items: center;
        position: relative;

        &-time {
            display: flex;
            flex-direction: column;
            padding: 7px 20px 3px;
            border: 1px solid $lineColor;
            position: absolute;
            left: 0;
            top: $timelineHeaderHeight;
            z-index: 1;
            background: #fff;
            text-align: center;
            font-weight: 600;
            color: $textColor;

            img {
                flex: 0 !important;
                width: 50px;
                margin: auto;
            }

            &-value {
                display: block;
                margin-top: 7px;
                white-space: nowrap;
                font-size: 16px;
            }
        }

        &-header {
            height: $timelineHeaderHeight;
            font-size: 20px;
            font-weight: 600;
            position: relative;
            z-index: 1;
            overflow: hidden;

            &-title {
                background: #fff;

                &-img {
                    flex: 0 !important;
                    height: 100px;
                }

                &-info {
                    font-size: 20px;
                    font-weight: 600;
                    color: $textColor;
                }
            }
        }

        &-bar {
            width: 100%;
            height: $timelineBarHeight;
            position: relative;
            background: $lineColor;
            margin-top: $timelineBarTopMargin;
            margin-bottom: $timelineBarBottomMargin;
            z-index: 0;
            border-radius: 100px;

            &-line {
                width: 250px;
                position: absolute;
                top: 7.5px;
                transform: translateY(-50%) translateX(-50%);
                left: 50%;

                &-top {
                    width: 0;
                    height: 200px;
                    border-right: 2px solid $lineColor;
                    margin: auto;
                }

                &-point {
                    width: 30px;
                    height: 30px;
                    background: #2d3136;
                    border: 4px solid #ffffff;
                    border-radius: 50%;
                    margin: auto;
                }

                &-bottom {
                    width: 0;
                    height: 200px;
                    border-right: 2px dashed $lineColor;
                    margin: auto;
                }
            }
        }

        &-description {
            width: 100%;
            min-height: 100px;
            background: #fff;
            color: $textColor;
            z-index: 1;
            position: relative;
            display: flex;
            justify-content: center;

            &-line {
                width: 100%;
                height: 0;
                border-bottom: 2px dashed $lineColor;
                position: absolute;
                left: 0;
                top: 50px;
                z-index: 0;

                &.short {
                    width: 50%;
                }
            }

            &-inner {
                width: fit-content;
                min-width: 250px;
                max-width: 100%;
                border: 1px solid $lineColor;
                padding: 10px;
                background: #fff;
                z-index: 1;
                position: relative;
                overflow-x: auto;

                .offer-model {
                    margin-left: -11px;
                    margin-right: -11px;
                    padding-left: 11px;
                    padding-right: 11px;
                }

                .transaction-marked-product .inner-wrapper {
                    margin-left: -11px;
                    padding-left: 11px;
                }
            }

            &-header {
                display: flex;
                align-items: center;
                margin-bottom: 15px;
                font-weight: 600;

                img {
                    height: 44px;
                }

                &-label {
                    margin-left: 10px;
                }
            }
        }

        &:first-child {
            z-index: 10;
        }

        &:last-child {
            .timeline-item-bar {
                &-line {
                    &-point {
                        background: #00e08f;
                    }
                }
            }
        }
    }

    @media only screen and (min-width: 1101px) {
        .timeline-item {
            width: 30%;

            &-bar {
                border-radius: 0;
            }

            &:first-child {
                align-items: flex-start;

                .timeline-item-bar {
                    border-radius: 0;
                    border-top-left-radius: 100px;
                    border-bottom-left-radius: 100px;

                    &-line {
                        left: 0;
                        right: auto;
                        transform: translateY(-50%) translateX(0);
                    }
                }

                .timeline-item-description {
                    justify-content: flex-start;
                }
            }

            &:last-child {
                align-items: flex-end;
                .timeline-item-bar {
                    border-radius: 0;
                    border-top-right-radius: 100px;
                    border-bottom-right-radius: 100px;

                    &-line {
                        left: auto;
                        right: 0;
                        transform: translateY(-50%) translateX(0);
                    }
                }

                .timeline-item-description {
                    justify-content: flex-end;
                }
            }
        }
    }

    @media only screen and (max-width: 1100px) {
        .timeline-wrapper {
            flex-direction: column;
        }

        .timeline-item {
            width: 100%;

            &-time {
                position: relative;
                top: 0;
                transform: translateY(-50%);
            }

            &-description {
                transform: translateY(-90px);

                &-line {
                    display: none;
                }
            }
        }
    }

    .offer-model {
        display: block;
        font-weight: 600;
        color: #2d3136;
    }

    .offer-ean {
        display: block;
    }

    .offer-ean--bold {
        font-weight: 600;
        color: #2d3136;
    }

    .transaction-table {
        width: 100%;
        table-layout: auto;
        thead td {
            min-width: 60px;
            padding-bottom: 10px;
            font-weight: 600;
            white-space: nowrap;
        }
        tbody td {
            padding-bottom: 5px;
            white-space: nowrap;

            &:last-child {
                text-align: center;
            }

            &:not(:last-child) {
                padding-right: 30px;
            }
        }
    }

    .transaction-marked-product {
        font-weight: 600;
        color: #2d3136;
    }
</style>
